import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Database,
  ClipboardList,
  BarChart3,
  Map,
  Upload,
  Download,
  ScrollText,
  Settings,
  Menu,
  X,
  Users,
  Cpu,
  Share2,
  Phone,
  Wifi,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { UserRole } from '../../types';
import clsx from 'clsx';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: UserRole[];
  alarmplanOnly?: boolean;
}

const allNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <LayoutDashboard size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE] },
  { label: 'Organisationen', path: '/organizations', icon: <Building2 size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE] },
  { label: 'Benutzer', path: '/users', icon: <Users size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE] },
  { label: 'Datenfelder', path: '/fields', icon: <Database size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Verarbeitungslogik', path: '/data-sources', icon: <Share2 size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'API-Endpunkte', path: '/api-endpoints', icon: <Cpu size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Fragebögen', path: '/questionnaires', icon: <ClipboardList size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Meine Fragebögen', path: '/my', icon: <ClipboardList size={20} /> },
  { label: 'Daten', path: '/data', icon: <Database size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR, UserRole.USER_VIEWER] },
  { label: 'Berichte', path: '/reports', icon: <BarChart3 size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR, UserRole.USER_VIEWER] },
  { label: 'Karten', path: '/maps', icon: <Map size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR, UserRole.USER_VIEWER] },
  { label: 'Import', path: '/import', icon: <Upload size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Export', path: '/export', icon: <Download size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Audit Log', path: '/logs', icon: <ScrollText size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE] },
  { label: 'Einstellungen', path: '/settings', icon: <Settings size={20} />, roles: [UserRole.ADMIN_GENERALSEKRETARIAT, UserRole.ADMIN_LANDESVERBAND, UserRole.ADMIN_BEREICH, UserRole.ADMIN_BEZIRKSSTELLE, UserRole.USER_EDITOR] },
  { label: 'Alarmplan', path: '/alarmplan', icon: <Phone size={20} />, alarmplanOnly: true },
];

export default function Sidebar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user } = useAuth();

  const navItems = useMemo(() => {
    if (!user) return [];
    return allNavItems.filter(item => (!item.roles || item.roles.includes(user.role)) && (!item.alarmplanOnly || user.alarmplanEnabled));
  }, [user]);

  const navContent = (
    <nav className="flex flex-1 flex-col" aria-label="Seitennavigation">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 px-6 border-b border-primary-700">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white overflow-hidden">
          <img
            src="/img/oerk-logo.png"
            alt="ÖRK"
            className="h-8 w-8 object-contain"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-white leading-tight">Rotes Kreuz</span>
          <span className="text-xs text-primary-200 leading-tight">Datenplattform</span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'text-primary-100 hover:bg-white/10 hover:text-white',
                  )
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <div className="border-t border-primary-700 px-4 py-3">
        <p className="text-xs text-primary-300 text-center">
          © {new Date().getFullYear()} Österreichisches Rotes Kreuz
        </p>
      </div>
    </nav>
  );

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        type="button"
        className="fixed top-3 left-3 z-50 flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white shadow-lg lg:hidden"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label={isMobileOpen ? 'Menü schließen' : 'Menü öffnen'}
      >
        {isMobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-64 bg-primary-800 shadow-xl transition-transform duration-300 lg:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {navContent}
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-primary-800">
        {navContent}
      </aside>
    </>
  );
}
